import _ from 'lodash'
import React from 'react'
import T from 'prop-types'
import cx from 'classnames'
import {
  TextOverflow
} from 'react-base'
import {
  TransitionGroup,
  CSSTransition
} from 'react-transition-group'
import { SCHEMA_ITEM_SHAPE } from '../../shapes'
import memoize from 'memoize-one'
import {
  getPositionFromEvent
} from '../../utils/eventUtils'

export const AXIS_SIDE_OPTIONS = _.mapValues({
  TOP: 1,
  RIGHT: 1,
  BOTTOM: 1,
  LEFT: 1
}, (v, k) => k)


export default class Axis extends React.Component {
  static propTypes = {
    scale: T.any.isRequired,
    side: T.oneOf(Object.keys(AXIS_SIDE_OPTIONS)),
    field: SCHEMA_ITEM_SHAPE.isRequired,
    fieldKey: T.string.isRequired,
    width: T.number,
    height: T.number,
    style: T.object,
    mousePos: T.number, //px
    horizontalTickWidth: T.number,
    verticalTickHeight: T.number
  }

  static defaultProps = {
    side: 'left',
    horizontalTickWidth: 85, // Includes desired margin
    verticalTickHeight: 30 // Includes desired margin
  }

  state = {
    selfMouseX: null,
    selfMouseY: null
  }

  handleMouseOver = e => {
    const {offsetX, offsetY} = getPositionFromEvent(this, e)

    this.setState({
      selfMouseX: offsetX,
      selfMouseY: offsetY
    })
  }

  handleMouseOut = () => {
    this.setState({
      selfMouseX: null,
      selfMouseY: null
    })
  }

  isHorizontalAxis = () => {
    const {side} = this.props
    return side === AXIS_SIDE_OPTIONS.TOP || side === AXIS_SIDE_OPTIONS.BOTTOM
  }

  getFormatter = memoize((field) => {
    return field.get('formatDisplay')
  })

  renderText = (value, field, formatter) => {
    const text = formatter ? formatter(value, field) : `${value}`
    const splitVal = typeof text === 'string' && text.indexOf(' ') !== -1 ? text.split(' ') : null // Split on first space, assuming [value, unit]

    return (
      <TextOverflow className="axis_text">
        {splitVal ? (
          <span className="split_text">
            <span className="split_val">{splitVal[0]}</span>
            <span className="split_unit">{splitVal[1]}</span>
          </span>
        ) : text}
      </TextOverflow>
    )
  }

  renderTicks = memoize((
    field,
    fieldKey,
    scale,
    height,
    width,
    sideClassName,
    dataTypeClassName,
    isHorizontalAxis,
    minOffset,
    maxOffset,
    tickPrimaryDimension
  ) => {
    const formatter = this.getFormatter(field)
    const usableLength = maxOffset - minOffset
    const ticks = _.uniq(
      scale.copy()
        .ticks(Math.floor(usableLength / tickPrimaryDimension)) // Estimated desired number of filler ticks
        .concat(scale.domain()) // Always render the scale's domain min/max
    )
    .sort((a, b) => a - b)
    .map((t, i, arr) => ({
      isFloor: i === 0,
      isCeil: i === arr.length - 1,
      tick: t,
      tickOffset: Math.floor(scale(t))
    }))
    .sort((a, b) => a.tickOffset - b.tickOffset)

    let _prevLabeledTickOffset = ticks[0].tickOffset

    return ticks.map(({tick, tickOffset, isFloor, isCeil}, i) => {
      const isTickLabeled = isFloor || isCeil || (
        tickOffset >= _prevLabeledTickOffset + tickPrimaryDimension // Show label if we're greater than one label-width away from the previous labeled tick
        && tickOffset <= maxOffset - (tickPrimaryDimension * (isHorizontalAxis ? 2 : 1)) // Make sure this tick does not overlap with the flipped-alignment ceil tick
      )

      _prevLabeledTickOffset = isTickLabeled ? tickOffset : _prevLabeledTickOffset

      return (
        <CSSTransition
          classNames={'fade'}
          timeout={{
            enter: 500,
            exit: 300
          }}
          key={`${i}_${tick}`}
        >
          <div
            className={cx(
              `schemer_axis_tick`,
              `tick_${sideClassName}`,
              `tick_${dataTypeClassName}`,
              isHorizontalAxis ? 'tick_horizontal' : 'tick_vertical',
              {
                tick_floor: isFloor,
                tick_ceil: isCeil,
                show_label: isTickLabeled
              }
            )}
            style={{
              transform: `translate${isHorizontalAxis ? 'X' : 'Y'}(${tickOffset}px)`,
            }}
          >{this.renderText(tick, field, formatter)}</div>
        </CSSTransition>
      )
    })
  })

  render() {
    const {
      scale,
      side,
      height,
      width,
      field,
      fieldKey,
      style,
      mousePos,
      verticalTickHeight,
      horizontalTickWidth
    } = this.props
    const {
      selfMouseX,
      selfMouseY
    } = this.state

    if (!scale) {
      return null
    }

    const formatter = this.getFormatter(field)
    const isHorizontalAxis = this.isHorizontalAxis()
    const tickPrimaryDimension = isHorizontalAxis ? horizontalTickWidth : verticalTickHeight
    const _mousePos = mousePos || (isHorizontalAxis ? selfMouseX : selfMouseY)
    const sideClassName = `side_${side.toLowerCase()}`
    const dataTypeClassName = `axis_type_${field.get('type').toLowerCase()}`
    const [range0, range1] = scale.range()
    const minOffset = Math.min(range0, range1)
    const maxOffset = Math.max(range0, range1)

    return (
      <div
        className={`schemer_axis ${sideClassName} ${dataTypeClassName}`}
        style={{
          height,
          width
        }}
        onMouseOver={this.handleMouseOver}
        onMouseMove={this.handleMouseOver}
        onMouseOut={this.handleMouseOut}
      >
        <div
          className={`schemer_axis_border axis_border_${sideClassName}`}
          style={isHorizontalAxis ? {
            left: Math.floor(range0),
            right: Math.floor(width - range1)
          } : {
            top: Math.floor(range1),
            bottom: Math.floor(height - range0)
          }}
        />
        <TransitionGroup
          component="div"
          style={style}
        >
          {this.renderTicks(
            field,
            fieldKey,
            scale,
            height,
            width,
            sideClassName,
            dataTypeClassName,
            isHorizontalAxis,
            minOffset,
            maxOffset,
            tickPrimaryDimension
          )}
          {_mousePos && _mousePos >= minOffset && _mousePos <= maxOffset &&
            <CSSTransition
              classNames={'fade'}
              timeout={{
                enter: 500,
                exit: 300
              }}
              key={`_hover_tick`}
            >
              <div
                className={
                  `axis_mouse_ruler ${isHorizontalAxis ? 'horiz' : 'vert'} ${_mousePos + tickPrimaryDimension > maxOffset ? 'shifted' : ''}`
                }
                style={{
                  transform: `translate${isHorizontalAxis ? 'X' : 'Y'}(${_mousePos}px)`,
                }}
              >
                <div
                  className={cx(
                    `schemer_axis_tick`,
                    `tick_${sideClassName}`,
                    `tick_${dataTypeClassName}`,
                    isHorizontalAxis ? 'tick_horizontal' : 'tick_vertical',
                    `show_label`
                  )}
                  style={{
                    [isHorizontalAxis ? 'width' : 'height']: tickPrimaryDimension
                  }}
                >
                  {this.renderText(scale.invert(_mousePos), field, formatter)}
                </div>
              </div>
            </CSSTransition>
          }
        </TransitionGroup>
      </div>
    )
  }
}
